import { useContext, useMemo } from 'react';
import { useSnackBar } from '@usealloy/component-library';
import { UserContext } from '~/dashboard/context';
import log from '~/helpers/logging';
import { useGetCustomers } from '~/dashboard/admin/provisioning/helpers/useGetCustomers';
import { useGetRbacCustomers } from '~/dashboard/admin/provisioning/helpers/useGetCustomerRbacData';
import { getTableData } from '~/dashboard/admin/provisioning/helpers/getTableData';
import { useGetChildCustomers } from '~/helpers/api-queries/embeddedFinance';
import { accessTypeLabelMap } from '~/dashboard/admin/provisioning/helpers/constants';
import { RBACAccessType } from '~/dashboard/admin/provisioning/types';
import type { AllCustomersRow } from './types';

const REQUEST_ACCESS_LABEL = accessTypeLabelMap[RBACAccessType.None];

/**
 * Whether a row can actually be acted on, and so is worth showing at all.
 *
 * `getTableData` sources both `access_type` and `customer_id` from the RBAC
 * response, defaulting the access type to `None` ("Request Access") when there
 * is no entry — so a cell whose best-effort RBAC request failed (see
 * `useGetRbacCustomers`, which swallows per-cell failures) yields rows that
 * read "Request Access" but carry no `customer_id` to request access *to*.
 * There is nothing a user can do with those, so they are filtered out rather
 * than rendered as unusable entries.
 *
 * Note this is deliberately scoped to the sidebar switcher: the full-page admin
 * Customer List calls `getTableData` directly and still shows every customer.
 */
export function isRowActionable(row: AllCustomersRow): boolean {
  return row.access_type !== REQUEST_ACCESS_LABEL || Boolean(row.customer_id);
}

/**
 * Composes the existing admin-provisioning data hooks (built for the full-page
 * Customer List view) to power the sidebar's "All Customers" search section.
 * All fetches are gated on `enabled` so an admin loading any page doesn't
 * trigger the provisioning-lambda call or the per-cell RBAC fan-out until
 * they actually open the switcher popover.
 */
export const useAllCustomersData = (enabled: boolean): {
  rows: AllCustomersRow[];
  isLoading: boolean;
  isError: boolean;
} => {
  const { agent } = useContext(UserContext);
  const addSnackBar = useSnackBar();

  const {
    data: { cells, customers } = { cells: {}, customers: [] },
    isLoading: isCustomersLoading,
    isError: isCustomersError,
  } = useGetCustomers({
    enabled: enabled && agent?.email !== undefined,
    onError: error => {
      log('error', 'Error fetching provisioning data for sidebar customer switcher', {}, error);
      addSnackBar({ title: 'Unexpected error loading customer data', type: 'danger' });
    },
  });

  const {
    data: customerRbacData = [],
    isLoading: isRbacQueryLoading,
    isError: isRbacError,
    fetchStatus: rbacFetchStatus,
  } = useGetRbacCustomers({ cells, agent }, {
    enabled: enabled && !!Object.keys(cells ?? {}).length,
    onError: error => {
      log('error', 'Error fetching RBAC data for sidebar customer switcher', {}, error);
      addSnackBar({ title: 'Unexpected error loading access data', type: 'danger' });
    },
  });

  const {
    data: childCustomersData,
    isLoading: isChildCustomersLoading,
    isError: isChildCustomersError,
  } = useGetChildCustomers({
    isParent: Boolean(agent?.isAfefParentAgent),
    enabled,
  });

  // react-query v4 reports a *disabled* query as `isLoading` (its status is
  // 'loading' until it has data, even when it never runs), so the RBAC query —
  // gated on `cells` being populated — would pin the section to
  // "Loading customers..." forever whenever the provisioning call comes back
  // with no cells. `fetchStatus` is what distinguishes "waiting on the network"
  // from "idle, never asked", the same guard `useGetChildCustomers` applies
  // internally.
  const isRbacLoading = isRbacQueryLoading && rbacFetchStatus !== 'idle';

  const rows = useMemo(() => getTableData({
    agent,
    customers,
    customerRbacData,
    childCustomers: childCustomersData?.child_customers,
  }).filter(isRowActionable), [agent, customers, customerRbacData, childCustomersData]);

  return {
    rows,
    isLoading: enabled && (isCustomersLoading || isRbacLoading || isChildCustomersLoading),
    isError: isCustomersError || isRbacError || isChildCustomersError,
  };
};
